import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { contactMergeDefaultListParams, contactMergeQueryRootKey } from "../config";
import {
  createContactMergeTask,
  fetchContactMergeCandidates,
  fetchContactMergeMetadata,
  fetchContactMergeTask,
  fetchContactMergeTasks,
  recognizeContactMergeImage,
  runContactMergeAction,
  updateContactMergeTask,
} from "../repo/contactMergeRepo";
import { recognitionSearchKeyword } from "../service/contactMergeService";
import type {
  ContactMergeEditorPayload,
  ContactMergeListParams,
  ContactMergeRecognition,
} from "../types";

/** 编排联系人合并任务列表、候选、截图识别和动作。 */
export function useContactMergeRuntime() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(contactMergeDefaultListParams.page);
  const [pageSize, setPageSize] = useState(contactMergeDefaultListParams.pageSize);
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState<string>();
  const [candidateSchoolName, setCandidateSchoolName] = useState("");
  const [candidateContactName, setCandidateContactName] = useState("");
  const [candidatePhone, setCandidatePhone] = useState("");
  const [submittedCandidateSchoolName, setSubmittedCandidateSchoolName] = useState("");
  const [submittedCandidateContactName, setSubmittedCandidateContactName] = useState("");
  const [submittedCandidatePhone, setSubmittedCandidatePhone] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState<number>();
  const [editorOpen, setEditorOpen] = useState(false);
  const [recognition, setRecognition] = useState<ContactMergeRecognition>();
  const [recognitionErrorMessage, setRecognitionErrorMessage] = useState("");

  const listParams: ContactMergeListParams = useMemo(
    () => ({
      page,
      pageSize,
      keyword: keyword.trim() || undefined,
      status,
    }),
    [keyword, page, pageSize, status],
  );
  const listQuery = useQuery({
    queryKey: [...contactMergeQueryRootKey, "list", listParams],
    queryFn: () => fetchContactMergeTasks(listParams),
  });
  const metadataQuery = useQuery({
    queryKey: [...contactMergeQueryRootKey, "metadata"],
    queryFn: fetchContactMergeMetadata,
  });
  const detailQuery = useQuery({
    queryKey: [...contactMergeQueryRootKey, "detail", selectedTaskId],
    queryFn: () => fetchContactMergeTask(selectedTaskId ?? 0),
    enabled: Boolean(selectedTaskId),
  });
  const submittedCandidateSearch = {
    schoolName: submittedCandidateSchoolName.trim() || undefined,
    contactName: submittedCandidateContactName.trim() || undefined,
  };
  const primaryCandidateSearchEnabled = Boolean(
    submittedCandidateSearch.schoolName || submittedCandidateSearch.contactName,
  );
  const candidatesQuery = useQuery({
    queryKey: [
      ...contactMergeQueryRootKey,
      "candidates",
      submittedCandidateSchoolName,
      submittedCandidateContactName,
    ],
    queryFn: () => fetchContactMergeCandidates(submittedCandidateSearch),
    enabled: primaryCandidateSearchEnabled,
  });
  const submittedPhone = submittedCandidatePhone.trim() || undefined;
  const phoneCandidateSearchEnabled = Boolean(submittedPhone);
  const phoneCandidatesQuery = useQuery({
    queryKey: [...contactMergeQueryRootKey, "candidates", "phone", submittedPhone],
    queryFn: () => fetchContactMergeCandidates({ phone: submittedPhone }),
    enabled: phoneCandidateSearchEnabled,
  });
  const primaryCandidateIds = new Set(
    (candidatesQuery.data ?? []).map((candidate) => candidate.contactId),
  );
  const phoneFallbackApplied = Boolean(
    phoneCandidateSearchEnabled &&
      phoneCandidatesQuery.data?.some(
        (candidate) => !primaryCandidateIds.has(candidate.contactId),
      ),
  );
  const candidates = useMemo(() => {
    const primaryCandidates = candidatesQuery.data ?? [];
    return Array.from(
      new Map(
        [...primaryCandidates, ...(phoneCandidatesQuery.data ?? [])].map((candidate) => [
          candidate.contactId,
          candidate,
        ]),
      ).values(),
    );
  }, [candidatesQuery.data, phoneCandidatesQuery.data]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: contactMergeQueryRootKey });

  const recognizeMutation = useMutation({
    mutationFn: recognizeContactMergeImage,
    onMutate: () => {
      setRecognition(undefined);
      setRecognitionErrorMessage("");
    },
    onSuccess: (result) => {
      setRecognition(result);
      setRecognitionErrorMessage("");
      const nextSearch = recognitionSearchKeyword(result);
      const nextSchoolName = nextSearch.schoolName ?? "";
      const nextContactName = nextSearch.contactName ?? "";
      const nextPhone = result.phones[0]?.trim() ?? "";
      setCandidateSchoolName(nextSchoolName);
      setCandidateContactName(nextContactName);
      setCandidatePhone(nextPhone);
      setSubmittedCandidateSchoolName(nextSchoolName);
      setSubmittedCandidateContactName(nextContactName);
      setSubmittedCandidatePhone(nextPhone);
    },
    onError: (error) => {
      setRecognitionErrorMessage(toRecognitionErrorMessage(error));
    },
  });
  const createMutation = useMutation({
    mutationFn: (payload: ContactMergeEditorPayload) =>
      createContactMergeTask(payload, recognition),
    onSuccess: async (detail) => {
      setSelectedTaskId(detail.id);
      setEditorOpen(false);
      setRecognition(undefined);
      await invalidate();
    },
  });
  const updateMutation = useMutation({
    mutationFn: (payload: ContactMergeEditorPayload) => {
      const detail = detailQuery.data;
      if (!detail) throw new Error("请先选择任务");
      return updateContactMergeTask(detail.id, detail.version, payload);
    },
    onSuccess: async (detail) => {
      queryClient.setQueryData(
        [...contactMergeQueryRootKey, "detail", detail.id],
        detail,
      );
      setEditorOpen(false);
      await invalidate();
    },
  });
  const actionMutation = useMutation({
    mutationFn: ({ action, reason }: { action: string; reason?: string }) => {
      const detail = detailQuery.data;
      if (!detail) throw new Error("请先选择任务");
      return runContactMergeAction(detail.id, toEndpointAction(action), {
        version: detail.version,
        previewDigest:
          action === "execute_direct"
            ? detail.approvedPreviewDigest ?? detail.previewDigest
            : action === "execute" || action === "retry"
              ? detail.approvedPreviewDigest
              : detail.previewDigest,
        reason,
        confirmConflicts: action === "resolve_exception",
      });
    },
    onSuccess: async (detail) => {
      queryClient.setQueryData(
        [...contactMergeQueryRootKey, "detail", detail.id],
        detail,
      );
      await invalidate();
    },
  });

  return {
    page,
    pageSize,
    keyword,
    status,
    candidateSchoolName,
    candidateContactName,
    candidatePhone,
    selectedTaskId,
    editorOpen,
    recognition,
    recognitionPending: recognizeMutation.isPending,
    recognitionErrorMessage,
    tasks: listQuery.data?.items ?? [],
    total: listQuery.data?.total ?? 0,
    candidates,
    phoneFallbackApplied,
    detail: detailQuery.data,
    metadata: metadataQuery.data,
    loading: listQuery.isLoading || detailQuery.isLoading,
    candidateLoading:
      candidatesQuery.isLoading ||
      candidatesQuery.isFetching ||
      phoneCandidatesQuery.isLoading ||
      phoneCandidatesQuery.isFetching,
    candidateSearchSubmitted:
      primaryCandidateSearchEnabled || phoneCandidateSearchEnabled,
    mutating:
      recognizeMutation.isPending ||
      createMutation.isPending ||
      updateMutation.isPending ||
      actionMutation.isPending,
    errorMessage:
      (listQuery.error as Error | null)?.message ??
      (detailQuery.error as Error | null)?.message ??
      (candidatesQuery.error as Error | null)?.message ??
      (phoneCandidatesQuery.error as Error | null)?.message ??
      (actionMutation.error as Error | null)?.message ??
      "",
    saveErrorMessage: toContactMergeSaveErrorMessage(
      createMutation.error ?? updateMutation.error,
    ),
    setKeyword,
    setStatus(nextStatus?: string) {
      setStatus(nextStatus);
      setPage(1);
    },
    setCandidateSchoolName,
    setCandidateContactName,
    setCandidatePhone,
    setSelectedTaskId,
    setEditorOpen,
    clearRecognition() {
      setRecognition(undefined);
      setRecognitionErrorMessage("");
      recognizeMutation.reset();
    },
    handleSearchCandidates() {
      const nextSchoolName = candidateSchoolName.trim();
      const nextContactName = candidateContactName.trim();
      const nextPhone = candidatePhone.trim();
      const isSameSearch =
        nextSchoolName === submittedCandidateSchoolName &&
        nextContactName === submittedCandidateContactName &&
        nextPhone === submittedCandidatePhone;
      if (isSameSearch && (nextSchoolName || nextContactName || nextPhone)) {
        if (nextSchoolName || nextContactName) void candidatesQuery.refetch();
        if (nextPhone) void phoneCandidatesQuery.refetch();
        return;
      }
      setSubmittedCandidateSchoolName(nextSchoolName);
      setSubmittedCandidateContactName(nextContactName);
      setSubmittedCandidatePhone(nextPhone);
    },
    handlePageChange(nextPage: number, nextPageSize?: number) {
      setPage(nextPage);
      if (nextPageSize && nextPageSize !== pageSize) {
        setPageSize(nextPageSize);
        setPage(1);
      }
    },
    async handleRecognizeImage(file: File) {
      try {
        return await recognizeMutation.mutateAsync(file);
      } catch {
        return undefined;
      }
    },
    handleCreate(payload: ContactMergeEditorPayload) {
      return createMutation.mutateAsync(payload);
    },
    handleUpdate(payload: ContactMergeEditorPayload) {
      return updateMutation.mutateAsync(payload);
    },
    handleAction(action: string, reason?: string) {
      return actionMutation.mutateAsync({ action, reason });
    },
  };
}

/** 将视觉服务错误转换为可操作的中文提示。 */
function toRecognitionErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? "");
  if (message.includes("vision-provider-not-configured")) {
    return "视觉模型未配置，请联系管理员配置联系人截图识别模型。";
  }
  return message || "图片识别失败，请稍后重试或改用手工搜索。";
}

/** 将保存任务领域错误转换为编辑器底部可操作的中文提示。 */
function toContactMergeSaveErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? "");
  if (message === "primary-sales-owner-selection-required") {
    return "同校主销售数量无法唯一判定，请选择最终所属销售后重试。";
  }
  if (message === "primary-sales-owner-selection-invalid") {
    return "所选所属销售不属于本次合并联系人，请重新选择。";
  }
  const conflictCode = "contact-in-active-merge-task";
  if (message === conflictCode || message.startsWith(`${conflictCode}:`)) {
    const taskNos = message
      .slice(conflictCode.length + 1)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
    const taskSummary = taskNos.length > 0 ? `，任务号：${taskNos.join("、")}` : "";
    return `这些联系人已存在于未完成的合并任务中${taskSummary}。`;
  }
  return message;
}

/** 将前端动作键转换为 API 子路径。 */
function toEndpointAction(action: string): string {
  return (
    {
      submit_review: "submit-review",
      execute_direct: "execute-direct",
      resolve_exception: "resolve-exception",
    }[action] ?? action
  );
}
